<template>
  <transition name="_fade" mode="out-in" >
    <div>
      <DataTableFilter ref="datatablefilter"
                       v-if="enableFilter"
                       :cache-id="cacheId"
                       @filterReady="filterReady"
                       :options="options"/>
      <div class="small text-muted mb-3"
           v-if="options.showTotalRecords && paginator">
        {{ $t('records')}}: {{ formatNumber(paginator.total) }}
      </div>
      <div class="mb-3" v-if="hiddenColumns && hiddenColumns.length">
        <span class="text-muted mr-2">{{ $t('hidden_columns') }}:</span>
        <span v-for="c in hiddenColumns"
              @click="toggleColumn(c)"
              class="tags-note tag-code pointer mr-2"
              :key="`hidden-col-${c}`">{{ $t(c) }}
        </span>
      </div>
      <div class="position-relative data-table-superwrapper">
        <div class="data-table-wrapper"
             ref="scrollEl"
             @scroll="checkScroll"
             :class="{
             manycolumns: options.fixedSizeTable,
           }">
          <table class="table data-table"
                 :class="[{
                  'more-button': needsMoreButton,
                  'wi-auto': wiAuto,
                  'fixedSizeTable': options.fixedSizeTable,
                  canHideRows: canHideRows || options.fixedSizeTable,
                  'notScrolled': notScrolled,
                  'tableFullyScrolled': tableFullyScrolled
                 },
                 'data-table-'+options.model
               ]">
            <thead>
            <tr :class="`order-${sort}`">
              <th v-if="showChecks"
                  class="checkboxed">
                <input @click="checkboxAll"
                       v-model="checkboxMain"
                       type="checkbox"/>
              </th>
              <DataTableHeaderCell v-for="(f, find) in visibleHeaderColumns"
                                    :field="f"
                                    :order="order"
                                    :options="options"
                                    :hiddenColumns="hiddenColumns"
                                    :pinPointColumns="pinPointColumns"
                                    :canHideRows="canHideRows"
                                    :pinnedLeft="pinnedLeft"
                                    @resort="resort"
                                    @toggle-column="toggleColumn"
                                    @pin-column="pinPointColumn"
                                    :key="`data-table-header-${f.id}-${find}`"/>
              <th class="actions no-print actions-th-header"
                  :class="{ 'text-center': options.fixedSizeTable }"
                  v-if="(options.actions && options.actions.some(r => ['edit','delete'].includes(r))) || options.actionscomponent">
                {{ !options.fixedSizeTable ? $t('actions') : '' }}
                <DataTableColumnSettingsModal v-if="options.fixedSizeTable"
                                              @refreshColumnSettings="refreshColumnSettings"
                                              :options="options"/>
              </th>
            </tr>
            </thead>
            <tbody>
            <template v-if="options.trcomponent">
              <component v-for="(l, lind) in list"
                         :is="trcomponents[options.trcomponent]"
                         :key="`custom-tr-${lind}`"
                         :object="l">
                <ActionButtons :options="options"
                               :index="lind"
                               :getTableHeight="getTableHeight"
                               :rowsCount="list ? list.length : 0"
                               :key="`custom-tr-actions-${lind}`"
                               :object="l"
                               :module="module"/>
              </component>
            </template>
            <tr v-else
                v-for="(l, lind) in list"
                :class="[
                options.trclass ? options.trclass.reduce((acc,curr)=> (acc[curr.class]=l[curr.condition],acc),{}) : '',
                { 'row-clickable': options.rowClickFunction, 'checked': selected.includes(l.id) }
              ]"
                @click="options.rowClickFunction ? onRowClickFunction($event, l) : null"
                :key="`data-table-${lind}`">
              <td v-if="showChecks">
                <input type="checkbox"
                       :value="l.id"
                       v-model="selected"
                       @click="checkboxChecked($event, lind)"/>
              </td>
              <template v-for="(f, find) in orderedColumns">
                <td :class="[
                    f.tabletdclass,
                    `size-${f.tdSize || 'default'}`,
                    {
                      hidden: hiddenColumns.includes(f.id),
                      pinned: pinPointColumns.includes(f.id),
                    }
                  ]"
                    :style="{ left: pinnedLeft(f) }"
                    :data-title="$t(f.title)"
                    v-if="showTdInTable(f)"
                    :key="`data-table-${lind}-${f.id}-${find}`">
                  <component :is="tdcomponents[f.tdcomponent] ? tdcomponents[f.tdcomponent] : 'TDComponent'"
                             :object="l"
                             :index="lind"
                             :field="f"/>
                </td>
              </template>

              <MobileMoreArrow v-if="needsMoreButton"/>

              <ActionButtons :options="options"
                             :index="lind"
                             :getTableHeight="getTableHeight"
                             :rowsCount="list ? list.length : 0"
                             :object="l"
                             :module="module"/>
            </tr>
            </tbody>
          </table>
        </div>
        <div class="sticky-header-clone"
             v-if="options.stickyHeader && stickyHeaderActive"
             :style="{ left: stickyHeaderRect.left + 'px', width: stickyHeaderRect.width + 'px' }">
          <table class="table data-table" :class="'data-table-'+options.model">
            <thead>
            <tr :class="`order-${sort}`" ref="stickyHeaderRow">
              <th v-if="showChecks"
                  class="checkboxed"
                  :style="{ width: stickyHeaderColWidths[0] + 'px' }"></th>
              <DataTableHeaderCell v-for="(f, find) in visibleHeaderColumns"
                                    :field="f"
                                    :order="order"
                                    :options="options"
                                    :hiddenColumns="hiddenColumns"
                                    :sticky="true"
                                    :width="stickyHeaderColWidths[(showChecks ? 1 : 0) + find]"
                                    @resort="resort"
                                    :key="`data-table-sticky-header-${f.id}-${find}`"/>
              <th class="actions no-print actions-th-header"
                  :class="{ 'text-center': options.fixedSizeTable }"
                  :style="{ width: stickyHeaderColWidths[stickyHeaderColWidths.length - 1] + 'px' }"
                  v-if="(options.actions && options.actions.some(r => ['edit','delete'].includes(r))) || options.actionscomponent">
                {{ !options.fixedSizeTable ? $t('actions') : '' }}
              </th>
            </tr>
            </thead>
          </table>
        </div>
        <a class="scroll-helper-button to-left"
           v-if="showLeftScrollHelper"
           @mouseenter="startScroll('left')"
           @mouseleave="stopScroll">
          <LeftSvg/>
        </a>
        <a class="scroll-helper-button to-right"
           v-if="showRightScrollHelper"
           @mouseenter="startScroll('right')"
           @mouseleave="stopScroll">
          <RightSvg/>
        </a>
      </div>

      <Paginator v-if="paginator && !loading"
                 :perPage="pagination"
                 :showPerPage="options.showTotalRecords"
                 :showAllOption="options.showAllOption"
                 @perPageChanged="perPageChanged"
                 :paginator="paginator"/>

      <SelectedBottomBar v-if="showChecks && hasBottomBarSlot"
                         @reset-selection="cancelSelection"
                         :selected="selected">
        <slot name="bottom-bar"/>
      </SelectedBottomBar>

    </div>
  </transition>
</template>

<script>
import { EloquentService } from "@/services/api/eloquent.api.service"
import Paginator from "./Paginator"
import { mapState } from "vuex"
import TDComponent from "@/components/parts/datatable/TDComponent"
import ActionButtons from "@/components/parts/datatable/ActionButtons"
import DataTableFilter from "@/components/parts/datatable/DataTableFilter"
import DataTableHeaderCell from "@/components/parts/datatable/DataTableHeaderCell.vue"
import MobileMoreArrow from "@/components/parts/datatable/MobileMoreArrow"
import { formatNumber } from "@/extensions/filters/filters"
import CogSvg from '@/assets/svg-vue/general/cog.svg'
import RightSvg from '@/assets/svg-vue/general/arrow_right.svg'
import LeftSvg from '@/assets/svg-vue/general/arrow_left.svg'
import SelectedBottomBar from "@/components/parts/datatable/SelectedBottomBar.vue"
import { copyObject } from "@/extensions/prototypes/prototypes"
import DataTableColumnSettingsModal from "@/components/parts/datatable/DataTableColumnSettingsModal.vue"
import { orderDataTableColumns } from "@/services/dataTable.service"

export default {
  name: "DataTable",
  components: {
    DataTableColumnSettingsModal,
    SelectedBottomBar,
    CogSvg,
    MobileMoreArrow,
    DataTableFilter,
    DataTableHeaderCell,
    ActionButtons,
    TDComponent,
    Paginator,
    RightSvg,
    LeftSvg
  },
  emits: ['totalRows', 'allParams', 'checkboxSelected'],
  props: {
    options: {
      type: Object
    },
    module: {
      type: String
    },
    resource: {
      type: String,
      default: ''
    },
    collection: {
      type: String,
      default: ''
    },
    params: {
      type: Object
    },
    wiAuto: {
      type: Boolean,
      default: false
    },
    loadOnInit: {
      type: Boolean,
      default: true
    },
    canHideRows: {
      type: Boolean,
      default: false
    },
    enableFilter: {
      type: Boolean,
      default: true
    },
    cacheId: {
      type: String,
      default: null
    },
  },
  data() {
    return {
      page: this.$route.query.page ? this.$route.query.page : 1,
      list: [],
      paginator: null,
      order: 'id',
      sort: 'asc',
      timer: null,
      loading: false,
      pagination: 10,
      filterWatcher: false,
      initialFilterReady: false,
      tableFullyScrolled: false,
      notScrolled: true,
      selected: [],
      checkboxMain: false,
      lastCheckedIndex: null,
      scrollDirection: null,
      hiddenColumns: window.localStorage.getItem(this.$route.name+'_hiddencolumns') || "[]",
      pinPointColumns: window.localStorage.getItem(this.$route.name+'_pinpointcolumns') || "[]",
      orderColumns: window.localStorage.getItem(this.$route.name+'_ordercolumns') || "[]",
      stickyHeaderActive: false,
      stickyHeaderRect: { left: 0, width: 0 },
      stickyHeaderColWidths: [],
      stickyHeaderRaf: null,
    }
  },
  mounted() {
    if(this.options.sort) this.sort = this.options.sort
    if(this.options.order) this.order = this.options.order
    if(this.sortCached) {
      if(this.sortCached.sort) this.sort = this.sortCached.sort
      if(this.sortCached.order) this.order = this.sortCached.order
    }
    if(this.options.pagination) this.pagination = this.options.pagination
    this.$store.commit('setSearch','')
    this.parseFilters()
    if(this.loadOnInit) this.load()
    // console.log(this.options.model)
    this.$store.commit('setViewModel', this.options.model)
    this.$root.$on('bv::modal::hidden', (bvEvent, modalId) => {
      if(modalId === 'form-modal') {
        this.$store.commit("setViewModel", this.options.model)
      }
    })
    this.refreshColumnSettings()
    // this.hiddenColumns = this.hiddenColumns ? JSON.parse(this.hiddenColumns) : []
    // this.pinPointColumns = this.pinPointColumns ? JSON.parse(this.pinPointColumns) : []
    this.$nextTick(() => {
      this.checkScroll()
      window.addEventListener('resize', this.checkScroll)
      if(this.options.stickyHeader) {
        window.addEventListener('scroll', this.onWindowScroll, { passive: true })
        window.addEventListener('resize', this.measureStickyHeader)
        this.measureStickyHeader()
      }
    })
  },
  methods: {
    formatNumber,
    load() {
      this.list = []
      this.loading = true
      EloquentService.dataTable(this.options.model, this.page, this.generateParams(), true, this.superadmin).then(res => {
        this.loading = false
        if(this.options.customListSorting) {
          this.list = this.options.customListSorting(res.data.result.data)
        } else {
          this.list = res.data.result.data
        }
        this.paginator = res.data.result
        this.$emit('totalRows', res.data.result.total)
      }).catch(() => this.loading = false)
    },
    removeConflictionConditionsAndFilters() {
      let params = copyObject(this.params)
      let paramKeys = params ? Object.keys(params) : []
      let filterKeys = this.filters ? Object.keys(this.filters) : []
      let commonKeys = paramKeys.filter(pk => filterKeys.includes(pk))
      commonKeys.forEach(ck => delete params[ck])
      return params
    },
    generateParams() {
      let params = this.removeConflictionConditionsAndFilters()
      return {
        condition:   params,
        sort:        this.sort,
        order:       this.order,
        pagination:  this.pagination,
        search:      this.search,
        filters:     this.filters,
        branch_id:   this.branch,
        resource:    this.resource,
        collection:  this.collection
      }
    },
    emitAllParams() {
      let params = this.generateParams()
      params.page = this.page
      params.model = this.options.model
      this.$emit('allParams', params)
    },
    resort(f) {
      if(!f.sortable) return
      const sortable_field = f.sortable === true ? f.id : f.sortable
      if(this.order === sortable_field) {
        this.sort = this.sort === 'asc' ? 'desc': 'asc'
      } else {
        this.sort = 'asc'
      }
      this.order = sortable_field
      this.$store.commit('setSortingCache', {
        id: this.cacheKey,
        value: {
          sort: this.sort,
          order: this.order
        }
      })
      // }
      this.load()
    },
    parseFilters() {
      let payload = {}
      if(this.$route.query.filters) {
        payload = JSON.parse(this.$route.query.filters)
      }
      this.$store.commit('setFilters', payload)
      setTimeout(() => this.filterWatcher = true, 200)
    },
    getTableHeight() {
      return this.$refs?.scrollEl?.getBoundingClientRect() || null
    },
    checkboxChecked(event, index) {
      if (event.shiftKey && (this.lastCheckedIndex !== null) && (this.lastCheckedIndex !== index)) {
        const dir = index > this.lastCheckedIndex ? 1 : -1
        for (let i = (this.lastCheckedIndex + dir); i !== index; i += dir) {
          let id = this.list[i].id
          if(!this.selected.includes(id)) {
            this.selected.push(id)
          } else {
            this.selected.splice(this.selected.indexOf(id), 1)
          }
        }
      }
      this.lastCheckedIndex = index
    },
    checkboxAll(e) {
      let checked = e.target.checked
      if(checked) {
        this.selected = this.list.map(x => x.id)
      } else {
        this.selected = []
      }
    },
    cancelSelection() {
      this.selected = []
      this.checkboxMain = false
    },
    perPageChanged(val) {
      if(this.pagination != val) {
        this.pagination = val
        this.load()
      }
    },
    filterChange(filter, action = 'add', condition = 1) {
      if(action == 'add') this.$refs.datatablefilter.filterSelected(filter, condition)
      if(action == 'delete') this.$refs.datatablefilter.removeFilter(filter.id)
      this.$refs.datatablefilter.refresh()
    },
    toggleColumn(id) {
      const index = this.hiddenColumns.indexOf(id)
      if (index > -1) {
        this.hiddenColumns.splice(index, 1)
      } else {
        this.hiddenColumns.push(id)
      }
      window.localStorage.setItem(this.$route.name+'_hiddencolumns', JSON.stringify(this.hiddenColumns))
      this.checkScroll()
    },
    pinPointColumn(id) {
      const index = this.pinPointColumns.indexOf(id)
      if (index > -1) {
        this.pinPointColumns.splice(index, 1)
      } else {
        this.pinPointColumns.push(id)
      }
      window.localStorage.setItem(this.$route.name+'_pinpointcolumns', JSON.stringify(this.pinPointColumns))
      this.checkScroll()
    },
    filterReady(res) {
      if(this.initialFilterReady) return
      this.initialFilterReady = true
      if(res === true) {
        this.load()
      }
    },
    showTdInTable(field) {
      if(!field.table) return false
      if(field.table instanceof Function) {
        if(!field.table(this.$store)) {
          return false
        }
      }
      if(field.right_excludes && this.rights.includes(field.right_excludes)) {
        return false
      }
      if(field.right_includes && !this.rights.includes(field.right_includes)) {
        return false
      }
      return true
    },
    pinnedLeft(field = null) {
      if(field && !this.pinPointColumns.includes(field.id)) return null
      let left = 0
      const mapping = {
        'xs': 100,
        'sm': 140,
        'md': 180,
        'lg': 220,
        'xl': 260,
        '2xl': 300,
      }
      for(const col of this.orderedColumns) {
        if(field && col.id === field.id) break
        if(this.pinPointColumns.includes(col.id)) {
          left += mapping[col.tdSize] || 100
        }
      }
      return left+'px'
    },
    refreshColumnSettings() {
      this.hiddenColumns = window.localStorage.getItem(this.$route.name+'_hiddencolumns') || "[]"
      this.hiddenColumns = this.hiddenColumns ? JSON.parse(this.hiddenColumns) : []
      this.pinPointColumns = window.localStorage.getItem(this.$route.name+'_pinpointcolumns') || "[]"
      this.pinPointColumns = this.pinPointColumns ? JSON.parse(this.pinPointColumns) : []
      this.orderColumns = window.localStorage.getItem(this.$route.name+'_ordercolumns') || "[]"
      this.orderColumns = this.orderColumns ? JSON.parse(this.orderColumns) : []
    },
    onRowClickFunction(e, object) {
      if(!this.options.rowClickFunction) return

      const selection = window.getSelection() // when someone selects text, do not trigger row click

      if (selection && selection.toString().length > 0) {
        return
      }

      const ignoredTags = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA']
      const ignoredClasses = ['action-buttons-td', 'action-button-mob-title', 'action-open-svg']

      if (ignoredTags.includes(e.target.tagName) || Array.from(e.target.classList).some(cls => ignoredClasses.includes(cls))) {
        return
      }

      this.options.rowClickFunction(object, this.$router, this.$store, this.$bvModal)
    },
    checkScroll() {
      const el = this.$refs.scrollEl
      if (!el) return

      const noScroll = el.scrollWidth <= el.clientWidth
      this.notScrolled = el.scrollLeft <= 0

      const atRight = Math.ceil(el.scrollLeft + el.clientWidth) >= el.scrollWidth

      this.tableFullyScrolled = noScroll || atRight
      this.syncStickyHeaderScroll()
    },
    syncStickyHeaderScroll() {
      // set directly on the DOM node instead of through reactive state, so the clone's
      // horizontal position never waits on a Vue render tick behind the native scroll
      const wrapperEl = this.$refs.scrollEl
      const rowEl = this.$refs.stickyHeaderRow
      if (!wrapperEl || !rowEl) return
      rowEl.style.transform = `translateX(-${wrapperEl.scrollLeft}px)`
    },
    onWindowScroll() {
      if (this.stickyHeaderRaf) return
      this.stickyHeaderRaf = requestAnimationFrame(() => {
        this.measureStickyHeader()
        this.stickyHeaderRaf = null
      })
    },
    measureStickyHeader() {
      if (this.mobile) {
        this.stickyHeaderActive = false
        return
      }
      const wrapperEl = this.$refs.scrollEl
      if (!wrapperEl) return
      const theadEl = wrapperEl.querySelector('thead')
      if (!theadEl) return

      const wrapperRect = wrapperEl.getBoundingClientRect()
      const theadHeight = theadEl.offsetHeight
      const shouldStick = wrapperRect.top <= 0 && wrapperRect.bottom > theadHeight

      if (shouldStick) {
        this.stickyHeaderRect = { left: wrapperRect.left, width: wrapperRect.width }
        const headerCells = theadEl.querySelectorAll('tr > *')
        this.stickyHeaderColWidths = Array.from(headerCells).map(el => el.getBoundingClientRect().width)
      }
      const wasActive = this.stickyHeaderActive
      this.stickyHeaderActive = shouldStick
      if (shouldStick && !wasActive) {
        this.$nextTick(this.syncStickyHeaderScroll)
      }
    },
    startScroll(direction = 'right') {
      this.scrollDirection = direction
      this.scroll()
    },
    stopScroll() {
      this.scrollDirection = null
    },
    scroll() {
      if (!this.scrollDirection) return

      const el = this.$refs.scrollEl
      if (!el) return

      const scrollCoefficient = this.scrollDirection === 'left' ? -1 : 1

      el.scrollLeft += 7 * scrollCoefficient

      if(el.scrollLeft <= 0) {
        this.scrollDirection = null
        return
      }
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth) {
        this.scrollDirection = null
        return
      }
      requestAnimationFrame(this.scroll)
    }
  },
  watch: {
    $route() {
      if(this.$route.query.page !== this.page) {
        this.page = this.$route.query.page
      }
      this.load()
    },
    search() {
      if(this.timer) {
        clearTimeout(this.timer)
      }
      this.timer = setTimeout(() => {
        this.page = 1
        this.load()
      }, 250)
    },
    counter() {
      this.load()
    },
    selected(val) {
      this.$emit('checkboxSelected', val)
    },
  },
  computed: {
    ...mapState({
      search: state => state.datatable.search,
      counter: state => state.datatable.dataTableCounter,
      filters: state => state.datatable.filters,
      sortingCache: state => state.datatable.sortingCache,
      superadmin: state => state.auth.superadmin,
      rights: state => state.auth.rights,
      branch: state => state.auth.branch,
    }),
    mobile() {
      return window && window.innerWidth < 768
    },
    needsMoreButton() {
      if(!this.options.fields) { return false }
      return (this.options.fields.reduce((acc, cur) => cur.table ? ++acc : acc, 0) > 3)
    },
    tdcomponents () {
      const components = require.context(`@/components/parts/datatable/td/`, true, /\.vue$/i)
      let arr = {}
      components.keys().map(x => {
        arr[x.split('/').pop().split('.')[0]] = components(x).default
      })
      return arr
    },
    trcomponents () {
      const components = require.context(`@/components/parts/datatable/tr/`, true, /\.vue$/i)
      let arr = {}
      components.keys().map(x => {
        arr[x.split('/').pop().split('.')[0]] = components(x).default
      })
      return arr
    },
    cacheKey() {
      return this.cacheId || this.$route.name
    },
    sortCached() {
      return this.sortingCache[this.cacheKey] ?? null
    },
    showChecks() {
      if(this.options.checksCondition) {
        return this.options.checksCondition(this.$store)
      }
      return this.options?.checks || false
    },
    hasBottomBarSlot() {
      return this.$slots['bottom-bar'] || this.$scopedSlots['bottom-bar']
    },
    orderedColumns() {
      return orderDataTableColumns(this.options.fields, this.orderColumns)
    },
    visibleHeaderColumns() {
      return this.orderedColumns.filter(f => this.showTdInTable(f))
    },
    showLeftScrollHelper() {
      if(!this.options.fixedSizeTable) return false
      if(this.notScrolled) return false
      return true
    },
    showRightScrollHelper() {
      if(!this.options.fixedSizeTable) return false
      if(this.tableFullyScrolled) return false
      return true
    },
    pinLeftMax() {
      return this.pinnedLeft()
    }
  },
  unmounted() {
    this.$store.commit('setEloquentOptions', null)
    window.removeEventListener('resize', this.checkScroll)
    if(this.options.stickyHeader) {
      window.removeEventListener('scroll', this.onWindowScroll)
      window.removeEventListener('resize', this.measureStickyHeader)
      if(this.stickyHeaderRaf) cancelAnimationFrame(this.stickyHeaderRaf)
    }
  }
}
</script>
